import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, Upload, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, track } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { DatePickerField } from "@/components/DatePickerField";
import { SelectField } from "@/components/SelectField";
import { waLink } from "@/constants/site";

const PROPERTY = ["Home", "Office", "Restaurant", "Hotel", "Retail", "Commercial", "Other"];
const SURFACE = ["Marble", "Granite", "Quartz", "Glass", "Wood", "Furniture", "Kitchen", "Bathroom", "Other"];

export default function Quote() {
  const location = useLocation();
  const navigate = useNavigate();
  const pre = location.state || {};
  const svc = pre.svc || null;
  const [form, setForm] = useState({
    name: "", mobile: "", email: "", location: "", pincode: "",
    property_type: pre.property_type || "", surface_type: pre.surface_type || "",
    area: "", preferred_service: pre.preferred_service || "", preferred_date: "", notes: "",
    recommended_service: pre.recommended_service || "", estimate: pre.estimate || "", material: pre.material || "",
  });
  const [details, setDetails] = useState({
    application: "", width: "", height: "", panels: "", quantity: "",
    design: "", finish: "", object_type: "", privacy_level: "",
  });
  const setD = (k, v) => setDetails((d) => ({ ...d, [k]: v }));
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { track("page_view", { page: "quote" }); }, []);

  const upload = async (files) => {
    for (const f of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, data.url]);
      } catch (_) { toast.error("Upload failed"); }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile) { toast.error("Name and mobile are required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/quotes", {
        ...form, photos, source: pre.source || "Website",
        scan_id: pre.scan?.id, product_id: pre.product_id,
        service_id: pre.service_id, category: pre.category,
        service_details: svc ? details : undefined,
      });
      track("quote_submitted", { surface: form.surface_type });
      setDone(data);
    } catch (_) { toast.error("Could not submit. Please try again."); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-24 text-center" data-testid="quote-success">
        <CheckCircle2 className="mx-auto text-[#10B981]" size={56} />
        <h1 className="font-display text-4xl mt-6">Request received</h1>
        <p className="mt-4 text-gray-400">Thank you, {done.name}. Your requirement has entered the ERRAH system. Our team will prepare an estimate and reach out shortly.</p>
        <div className="mt-6 hair-border bg-white/[0.02] p-6 text-left inline-block">
          <p className="errah-label">Estimated requirement</p>
          <p className="font-display text-xl mt-1">{form.surface_type || "Surface"} {form.preferred_service && `· ${form.preferred_service}`}</p>
          <p className="text-sm text-gray-500 mt-1">{form.property_type} {form.area && `· ~${form.area}`}</p>
        </div>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <a href={waLink(`Hi ERRAH, I just submitted a quote request for ${form.surface_type} protection.`)} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-[#C9A45C] text-black px-6 py-3 text-sm font-semibold hover:bg-[#E4C987] transition-colors">Chat on WhatsApp</a>
          <button onClick={() => navigate("/products")} className="border border-white/20 px-6 py-3 text-sm hover:bg-white hover:text-black transition-colors">Browse products</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-5 lg:px-10 py-16" data-testid="quote-page">
      <Seo title="Request a Quote" description="Request a surface protection quote from ERRAH." />
      <p className="errah-label gold-text mb-4">Smart quotation</p>
      <h1 className="font-display text-4xl lg:text-5xl font-light tracking-tighter">Request a quote.</h1>
      <p className="mt-4 text-gray-400">Share a few details and our AI helps classify your requirement. No exact price is promised until assessed.</p>

      {pre.scan && (
        <div className="mt-6 hair-border bg-white/[0.02] p-4 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="quote-scan-context">
          <span className="errah-label gold-text">From your scan</span>
          <span className="text-sm text-gray-200">{pre.scan.surface} · {pre.recommended_service || pre.scan.recommended_service}</span>
          {pre.estimate && <span className="text-sm text-gray-500">· {pre.estimate}</span>}
        </div>
      )}

      <form onSubmit={submit} className="mt-10 grid md:grid-cols-2 gap-4">
        <Input label="Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="q-name" />
        <Input label="Mobile *" value={form.mobile} onChange={(v) => setForm({ ...form, mobile: v })} testid="q-mobile" />
        <Input label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="q-email" />
        <Input label="Location / City" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="q-location" />
        <Input label="Pincode" value={form.pincode} onChange={(v) => setForm({ ...form, pincode: v })} testid="q-pincode" />
        <Input label="Approx. area (sq ft)" value={form.area} onChange={(v) => setForm({ ...form, area: v })} testid="q-area" />
        <SelectField label="Property type" value={form.property_type} onChange={(v) => setForm({ ...form, property_type: v })} options={PROPERTY} testid="q-property" />
        <SelectField label="Surface type" value={form.surface_type} onChange={(v) => setForm({ ...form, surface_type: v })} options={SURFACE} testid="q-surface" />
        <Input label="Preferred service" value={form.preferred_service} onChange={(v) => setForm({ ...form, preferred_service: v })} testid="q-service" />
        <DatePickerField label="Preferred date" value={form.preferred_date} onChange={(v) => setForm({ ...form, preferred_date: v })} testid="q-date" />

        {svc && (svc.category !== "Surface Protection") && (
          <div className="md:col-span-2 hair-border bg-white/[0.02] p-5 space-y-4" data-testid="q-service-details">
            <div className="flex items-center justify-between">
              <p className="errah-label gold-text">{svc.category} details</p>
              <span className="text-xs text-gray-500">{svc.service_name}</span>
            </div>
            {svc.applications?.length > 0 && (
              <SelectField label="Application / where" value={details.application} onChange={(v) => setD("application", v)} options={svc.applications} testid="q-application" />
            )}
            {svc.category === "Home & Object Vinyl Transformation" ? (
              <div className="grid md:grid-cols-2 gap-4">
                {svc.object_options?.length > 0 && <SelectField label="Object type" value={details.object_type} onChange={(v) => setD("object_type", v)} options={svc.object_options} testid="q-object" />}
                {svc.finish_options?.length > 0 && <SelectField label="Desired finish" value={details.finish} onChange={(v) => setD("finish", v)} options={svc.finish_options} testid="q-finish" />}
                <Input label="Number of objects" value={details.quantity} onChange={(v) => setD("quantity", v)} testid="q-quantity" />
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {svc.design_options?.length > 0 && <SelectField label="Desired design" value={details.design} onChange={(v) => setD("design", v)} options={svc.design_options} testid="q-design" />}
                <Input label="Approx. width (ft)" value={details.width} onChange={(v) => setD("width", v)} testid="q-width" />
                <Input label="Approx. height (ft)" value={details.height} onChange={(v) => setD("height", v)} testid="q-height" />
                <Input label="Number of panels" value={details.panels} onChange={(v) => setD("panels", v)} testid="q-panels" />
                <SelectField label="Privacy level" value={details.privacy_level} onChange={(v) => setD("privacy_level", v)} options={["Low", "Medium", "High", "Full"]} testid="q-privacy" />
              </div>
            )}
            <p className="text-xs text-gray-500" data-testid="q-measurement-note">
              Photos give an approximate estimate only. Final measurement is confirmed by ERRĀH or an authorised applicator.
            </p>
          </div>
        )}
        <div className="md:col-span-2">
          <label className="errah-label block mb-2">Additional notes</label>
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} data-testid="q-notes"
            className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[#C9A45C]" />
        </div>

        <div className="md:col-span-2">
          <label className="errah-label block mb-2">Photographs</label>
          <div className="flex flex-wrap gap-3">
            {photos.map((url, i) => (
              <div key={i} className="relative w-20 h-20 hair-border overflow-hidden">
                <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-black/70 p-0.5"><X size={12} /></button>
              </div>
            ))}
            <label className="w-20 h-20 hair-border flex items-center justify-center cursor-pointer hover:border-[#C9A45C] transition-colors" data-testid="q-photo-upload">
              <Upload size={18} className="text-gray-500" />
              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload(e.target.files)} />
            </label>
          </div>
        </div>

        <button type="submit" disabled={busy} data-testid="q-submit"
          className="md:col-span-2 inline-flex items-center justify-center gap-2 bg-[#C9A45C] text-black py-4 text-sm font-semibold hover:bg-[#E4C987] transition-colors disabled:opacity-60">
          {busy ? "Submitting…" : <>Submit request <ArrowRight size={16} /></>}
        </button>
      </form>
    </div>
  );
}

function Input({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="errah-label block mb-2">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[#C9A45C]" />
    </div>
  );
}
